import React, {useState} from 'react'
import {Contact} from '../type'
import {formatDateWithTimezone} from '../utils'
import {useTimezone} from '../../../../hooks/useTimezone'
import {THEME_COLORS} from '../../detail-wallet-card-report/colors'
import './ContactMobileCard.scss'
import { getAttentionStates } from './ContactTable'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'

interface ContactMobileAction {
  key: string
  label: string
  icon?: string
  danger?: boolean
  onClick: () => void
}

interface ContactMobileCardProps {
  contact: Contact
  isSelected: boolean
  onSelect: (id: string) => void
  onDelete: () => void
  onToggleSale: (contact: Contact, newStatus: boolean) => void
  actions?: ContactMobileAction[]
}

export const ContactMobileCard: React.FC<ContactMobileCardProps> = ({
  contact,
  isSelected,
  onSelect,
  onDelete,
  onToggleSale,
  actions = [],
}) => {
  const {userTimezone} = useTimezone()
  const [showActions, setShowActions] = useState(false)
  const navigate = useNavigate()

  // Get attention states for this contact
  const {shouldPulseVoice, shouldPulseMessage} = getAttentionStates(contact)
  const isSold = Boolean(contact.sales)
  const needsAttention = shouldPulseVoice || shouldPulseMessage

  const safeText = (value: any): string => {
    if (value == null) return ''
    if (typeof value === 'string' || typeof value === 'number') return String(value)
    return (
      value.name ?? value.title ?? value.label ?? value.displayName ?? value.id ?? value._id ?? ''
    )
  }

  const handleActionClick = () => {
    setShowActions(!showActions)
  }

  const getIntentBadgeStyle = (intent: string) => {
    switch (intent?.toLowerCase()) {
      case 'hot':
        return {backgroundColor: THEME_COLORS.DANGER, color: THEME_COLORS.WHITE}
      case 'warm':
        return {backgroundColor: THEME_COLORS.WARNING, color: THEME_COLORS.WHITE}
      case 'cold':
        return {backgroundColor: THEME_COLORS.GRAY_400, color: THEME_COLORS.GRAY_700}
      default:
        return {backgroundColor: THEME_COLORS.GRAY_300, color: THEME_COLORS.GRAY_600}
    }
  }

  const getSentimentBadgeStyle = (sentiment: string) => {
    switch (sentiment?.toLowerCase()) {
      case 'positive':
        return {backgroundColor: THEME_COLORS.SUCCESS_LIGHT, color: THEME_COLORS.SUCCESS}
      case 'negative':
        return {backgroundColor: THEME_COLORS.DANGER_LIGHT, color: THEME_COLORS.DANGER}
      case 'neutral':
        return {backgroundColor: THEME_COLORS.GRAY_200, color: THEME_COLORS.GRAY_600}
      default:
        return {backgroundColor: THEME_COLORS.GRAY_200, color: THEME_COLORS.GRAY_600}
    }
  }

  const totalSent = (contact as any)?.countSentMessages ?? contact.totalMessagesSent

  return (
    <div className={`lead-contact-mobile-card ${isSelected ? 'selected' : ''}`}>
      {/* FLOW BADGES: Mobile-only section at top */}
      {contact.flows && contact.flows.length > 0 && (
        <div className='mobile-flow-section d-block d-md-none mb-3'>
          <div className='d-flex flex-wrap gap-1'>
            {contact.flows.map((flow, index) => (
              <span
                key={index}
                className={`border rounded-pill px-3 py-1 fw-normal text-nowrap cursor-pointer d-inline-flex align-items-center justify-content-center flow-badge ${
                  flow.status === 'active'
                    ? 'text-primary bg-white border-primary'
                    : 'text-muted bg-light border-secondary'
                }`}
                onClick={() => {
                  if (flow) {
                    navigate(`/report-analytics/flow/report/${flow.id}`)
                  }
                }}
                title={flow.name}
              >
                <i
                  className={`bi ${
                    flow.status === 'active' ? 'bi-play-fill' : 'bi-pause-fill'
                  } me-1`}
                ></i>
                {flow.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* HEADER: Name + Checkbox + Actions */}
      <div className='card-header-section'>
        <div className='d-flex align-items-center gap-3 flex-grow-1'>
          <input
            type='checkbox'
            className='form-check-input contact-checkbox'
            checked={isSelected}
            onChange={() => onSelect(contact.id)}
            aria-label={`Select ${contact.fullName}`}
          />
          <div className='contact-name-section'>
            <div className='d-flex align-items-center gap-2'>
              <h5 className='contact-name'>{contact.fullName || 'N/A'}</h5>
              {/* Action Required Indicators */}
              {isSold ? (
                <div className='sold-indicator' title='Deal Status: SOLD'>
                  <i className='bi bi-patch-check-fill text-success'></i>
                </div>
              ) : needsAttention ? (
                <OverlayTrigger
                  placement='top'
                  delay={{show: 0, hide: 0}}
                  overlay={
                    <Tooltip>
                      {shouldPulseMessage
                        ? 'New reply received, but no message or call has been sent—please take action.'
                        : 'Add a voice note for this contact.'}
                    </Tooltip>
                  }
                >
                  <div
                    className='attention-indicator clickable-triangle'
                    onClick={() => {}}
                    role='button'
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                      }
                    }}
                    aria-label={shouldPulseMessage ? 'New reply received, but no message or call has been sent—please take action.' : 'Add a voice note for this contact.'}
                  >
                    <img
                      src='/media/svg/triangle_notify.svg'
                      alt='Attention'
                      className='attention-triangle'
                    />
                  </div>
                </OverlayTrigger>
              ) : null}
            </div>
            {/* Phone directly under name for quick access */}
            {contact.phone && (
              <div className='contact-phone text-muted small'>
                <i className='fas fa-phone me-1'></i>
                {contact.phone}
              </div>
            )}
          </div>
        </div>
        <button
          className={`btn btn-action-mobile ${needsAttention ? 'needs-attention' : ''} ${isSold ? 'sold-status' : ''}`}
          onClick={handleActionClick}
          aria-label='Actions menu'
          aria-expanded={showActions}
        >
          <i className='fas fa-ellipsis-v'></i>
          {/* Pulse ring for attention */}
          {needsAttention && (
            <div className='action-ring-pulse'></div>
          )}
        </button>
      </div>

      {/* Actions dropdown */}
      {showActions && (
        <div className='actions-dropdown'>
          <button
            className='action-item'
            onClick={() => {
              onToggleSale(contact, !contact.sales)
              setShowActions(false)
            }}
          >
            <i className={`fas fa-${contact.sales ? 'times' : 'check'} me-2`}></i>
            {contact.sales ? 'Mark as Not Sold' : 'Mark as Sold'}
          </button>
          {actions.map((a) => {

            const shouldPulse = (a.key === 'voice' && shouldPulseVoice) ||
                               (a.key === 'msg' && shouldPulseMessage)

            return (
              <button
                key={a.key}
                className={`action-item ${a.danger ? 'text-danger' : ''} ${shouldPulse ? 'pulsing-action' : ''}`}
                onClick={() => {
                  a.onClick()
                  setShowActions(false)
                }}
              >
                {a.icon && <i className={`${a.icon} me-2`}></i>}
                {a.label}
                {shouldPulse && <div className='action-pulse-dot'></div>}
              </button>
            )
          })}
          <button
            className='action-item text-danger'
            onClick={() => {
              onDelete()
              setShowActions(false)
            }}
          >
            <i className='fas fa-trash me-2'></i>
            Delete
          </button>
        </div>
      )}

      {/* STATUS BADGES ROW: Intent + Sentiment + Sales Status */}
      <div className='card-status-row mb-3 gap-2 d-flex'>
        {contact.intent && (
          <span className='badge' style={getIntentBadgeStyle(contact.intent)}>
            {contact.intent}
          </span>
        )}
        {contact.sentiment && (
          <span className='badge' style={getSentimentBadgeStyle(contact.sentiment)}>
            {contact.sentiment}
          </span>
        )}
        {contact.sales && <span className='badge bg-success text-white'>Sold</span>}
      </div>

      {/* CONTEXT INFO: Flow + Source + Assigned To */}
      <div className='card-context-row mb-3'>
        {contact.flows && contact.flows.length > 0 && (
          <div className='context-item mb-2 d-none d-md-block'>
            <i className='fas fa-stream me-1 text-primary'></i>
            <div className='d-flex flex-wrap gap-1'>
              {contact.flows.map((flow, index) => (
                <span
                  key={index}
                  className={`border rounded-pill px-3 py-1 fw-normal text-nowrap cursor-pointer d-inline-flex align-items-center justify-content-center flow-badge ${
                    flow.status === 'active'
                      ? 'text-primary bg-white border-primary'
                      : 'text-muted bg-light border-secondary'
                  }`}
                  onClick={() => {
                    if (flow) {
                      navigate(`/report-analytics/flow/report/${flow.id}`)
                    }
                  }}
                  title={flow.name}
                >
                  <i
                    className={`bi ${
                      flow.status === 'active' ? 'bi-play-fill' : 'bi-pause-fill'
                    } me-1`}
                  ></i>
                  {flow.name}
                </span>
              ))}
            </div>
          </div>
        )}
        {contact.source && (
          <div className='context-item mb-2'>
            <i className='bi bi-card-heading me-1 text-info'></i>
            <span>{safeText(contact.source)}</span>
          </div>
        )}
        {contact.assignedTo && (
          <div className='context-item mb-2'>
            <i className='fas fa-user-tie me-1 text-secondary'></i>
            <span>{safeText(contact.assignedTo)}</span>
          </div>
        )}
      </div>

      {/* CARD OFFERS: If multiple promo cards */}
      {contact.promoSettings && contact.promoSettings.length > 0 && (
        <div className='card-promo-section mb-3'>
          <div className='promo-label mb-2'>
            <i className='fas fa-credit-card me-1'></i>
            <span className='fw-semibold small text-muted'>Card Offers</span>
          </div>
          <div className='d-flex flex-wrap gap-2'>
            {contact.promoSettings.map((card: any, idx: number) => {
              const text =
                typeof card === 'string'
                  ? card
                  : card?.title ?? card?.name ?? card?.label ?? card?.id ?? 'Item'
              return (
                <span key={idx} className='badge bg-primary text-white'>
                  {text}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {/* DATE ADDED */}
      <div className='card-date-added text-muted small'>
dahboard-main        Date Added: {formatDateWithTimezone(contact.createdAt, 'DD/MM/YYYY', userTimezone)}
      </div>
    </div>
  )
}
